/**
 * 最小可用的 QR Code 编码器（字节模式，纠错等级 L / M，版本 1–10）。
 *
 * 为什么要自己写：两步验证的 otpauth 链接里**含有密钥**，把它交给任何第三方
 * 图表/二维码服务都等于把第二因子的种子送出站点；而为了一个二维码引一整个
 * 依赖也不划算。这里只实现绑定验证器 App 需要的这一档能力。
 *
 * 输出是模块矩阵（0/1），由 <QRCode> 渲染成 SVG —— 不依赖 canvas，也不产生位图。
 *
 * 正确性由 lib/qrcode.test.ts 用独立解码器（jsqr）做「编码 → 光栅化 → 解码」
 * 的往返验证，而不是靠肉眼比对图案。
 */

/** 纠错等级。M 约 15%，L 约 7%。 */
export type EccLevel = 'L' | 'M';

export interface QrMatrix {
  /** 每边的模块数（21 + 4×(版本-1)） */
  size: number;
  /** 行优先排列，1 = 深色模块 */
  modules: Uint8Array;
  version: number;
  ecc: EccLevel;
}

/** 每个纠错块的结构：`[数据码字数, 块数]`，可能有两组。 */
type BlockGroup = readonly [dataCodewords: number, blocks: number];

interface VersionSpec {
  /** 该版本总码字数（数据 + 纠错） */
  totalCodewords: number;
  /** 每个纠错块的纠错码字数 */
  ecCodewordsPerBlock: number;
  groups: readonly BlockGroup[];
}

/** 版本 1–10 的码字结构。索引 0 对应版本 1。 */
const ECC_TABLE: Record<EccLevel, readonly VersionSpec[]> = {
  L: [
    { totalCodewords: 26, ecCodewordsPerBlock: 7, groups: [[19, 1]] },
    { totalCodewords: 44, ecCodewordsPerBlock: 10, groups: [[34, 1]] },
    { totalCodewords: 70, ecCodewordsPerBlock: 15, groups: [[55, 1]] },
    { totalCodewords: 100, ecCodewordsPerBlock: 20, groups: [[80, 1]] },
    { totalCodewords: 134, ecCodewordsPerBlock: 26, groups: [[108, 1]] },
    { totalCodewords: 172, ecCodewordsPerBlock: 18, groups: [[68, 2]] },
    { totalCodewords: 196, ecCodewordsPerBlock: 20, groups: [[78, 2]] },
    { totalCodewords: 242, ecCodewordsPerBlock: 24, groups: [[97, 2]] },
    { totalCodewords: 292, ecCodewordsPerBlock: 30, groups: [[116, 2]] },
    {
      totalCodewords: 346,
      ecCodewordsPerBlock: 18,
      groups: [
        [68, 2],
        [69, 2],
      ],
    },
  ],
  M: [
    { totalCodewords: 26, ecCodewordsPerBlock: 10, groups: [[16, 1]] },
    { totalCodewords: 44, ecCodewordsPerBlock: 16, groups: [[28, 1]] },
    { totalCodewords: 70, ecCodewordsPerBlock: 26, groups: [[44, 1]] },
    { totalCodewords: 100, ecCodewordsPerBlock: 18, groups: [[32, 2]] },
    { totalCodewords: 134, ecCodewordsPerBlock: 24, groups: [[43, 2]] },
    { totalCodewords: 172, ecCodewordsPerBlock: 16, groups: [[27, 4]] },
    { totalCodewords: 196, ecCodewordsPerBlock: 18, groups: [[31, 4]] },
    {
      totalCodewords: 242,
      ecCodewordsPerBlock: 22,
      groups: [
        [38, 2],
        [39, 2],
      ],
    },
    {
      totalCodewords: 292,
      ecCodewordsPerBlock: 22,
      groups: [
        [36, 3],
        [37, 2],
      ],
    },
    {
      totalCodewords: 346,
      ecCodewordsPerBlock: 26,
      groups: [
        [43, 4],
        [44, 1],
      ],
    },
  ],
};

/** 校正图形的中心坐标（版本 1 没有校正图形）。 */
const ALIGNMENT_CENTERS: readonly (readonly number[])[] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
];

/** 版本信息（版本 ≥ 7 时写入），BCH(18,6) 的已知取值。 */
const VERSION_INFO: Record<number, number> = {
  7: 0x07c94,
  8: 0x085bc,
  9: 0x09a99,
  10: 0x0a4d3,
};

const FORMAT_INFO_MASK = 0x5412;
const MAX_VERSION = 10;

/* ------------------------------------------------------------------ *
 * GF(256) 与 Reed-Solomon
 * ------------------------------------------------------------------ */

const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);

(() => {
  let value = 1;
  for (let i = 0; i < 255; i += 1) {
    GF_EXP[i] = value;
    GF_LOG[value] = i;
    value <<= 1;
    // 本原多项式 x^8 + x^4 + x^3 + x^2 + 1 (0x11D)
    if (value & 0x100) value ^= 0x11d;
  }
  for (let i = 255; i < 512; i += 1) GF_EXP[i] = GF_EXP[i - 255] as number;
})();

function gfMultiply(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[((GF_LOG[a] as number) + (GF_LOG[b] as number)) % 255] as number;
}

/** 生成多项式 ∏(x - α^i)，i 从 0 到 degree-1。 */
function generatorPolynomial(degree: number): Uint8Array {
  let poly = new Uint8Array([1]);
  for (let i = 0; i < degree; i += 1) {
    const next = new Uint8Array(poly.length + 1);
    for (let j = 0; j < poly.length; j += 1) {
      next[j] = (next[j] as number) ^ (poly[j] as number);
      next[j + 1] = (next[j + 1] as number) ^ gfMultiply(poly[j] as number, GF_EXP[i] as number);
    }
    poly = next;
  }
  return poly;
}

/** 计算多项式除法的余数，即该块的纠错码字。 */
function reedSolomonRemainder(data: Uint8Array, ecCodewords: number): Uint8Array {
  const generator = generatorPolynomial(ecCodewords);
  const remainder = new Uint8Array(ecCodewords);

  for (const byte of data) {
    const factor = byte ^ (remainder[0] as number);
    remainder.copyWithin(0, 1);
    remainder[ecCodewords - 1] = 0;
    for (let i = 0; i < ecCodewords; i += 1) {
      remainder[i] = (remainder[i] as number) ^ gfMultiply(generator[i + 1] as number, factor);
    }
  }
  return remainder;
}

/* ------------------------------------------------------------------ *
 * 位流与码字
 * ------------------------------------------------------------------ */

class BitBuffer {
  readonly bits: number[] = [];

  put(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i -= 1) this.bits.push((value >>> i) & 1);
  }

  get length(): number {
    return this.bits.length;
  }

  /** 补齐到字节边界并转成码字。 */
  toCodewords(): Uint8Array {
    while (this.bits.length % 8 !== 0) this.bits.push(0);
    const out = new Uint8Array(this.bits.length / 8);
    for (let i = 0; i < out.length; i += 1) {
      let byte = 0;
      for (let j = 0; j < 8; j += 1) byte = (byte << 1) | (this.bits[i * 8 + j] as number);
      out[i] = byte;
    }
    return out;
  }
}

function dataCapacity(spec: VersionSpec): number {
  return spec.groups.reduce((sum, [data, blocks]) => sum + data * blocks, 0);
}

/** 按字节模式编码，返回交织后的最终码字序列。 */
function buildCodewords(bytes: Uint8Array, spec: VersionSpec, version: number): Uint8Array {
  const capacity = dataCapacity(spec);
  const buffer = new BitBuffer();

  buffer.put(0b0100, 4); // 字节模式
  // 字符计数指示符：版本 1–9 是 8 位，版本 10+ 是 16 位。
  buffer.put(bytes.length, version <= 9 ? 8 : 16);
  for (const byte of bytes) buffer.put(byte, 8);

  // 结束符最多 4 位，剩余空间不足时按实际长度补。
  const remaining = capacity * 8 - buffer.length;
  buffer.put(0, Math.min(4, remaining));

  const data = buffer.toCodewords();
  const padded = new Uint8Array(capacity);
  padded.set(data);
  // 交替填充 0xEC / 0x11，直到填满数据容量。
  for (let i = data.length, alternate = 0; i < capacity; i += 1, alternate += 1) {
    padded[i] = alternate % 2 === 0 ? 0xec : 0x11;
  }

  // 分块计算纠错码字。
  const dataBlocks: Uint8Array[] = [];
  const ecBlocks: Uint8Array[] = [];
  let offset = 0;
  for (const [blockDataLength, blockCount] of spec.groups) {
    for (let i = 0; i < blockCount; i += 1) {
      const block = padded.subarray(offset, offset + blockDataLength);
      dataBlocks.push(block);
      ecBlocks.push(reedSolomonRemainder(block, spec.ecCodewordsPerBlock));
      offset += blockDataLength;
    }
  }

  // 交织：先按列取数据码字，再按列取纠错码字。
  const result = new Uint8Array(spec.totalCodewords);
  let cursor = 0;
  const maxDataLength = Math.max(...dataBlocks.map((block) => block.length));
  for (let i = 0; i < maxDataLength; i += 1) {
    for (const block of dataBlocks) {
      if (i < block.length) result[cursor++] = block[i] as number;
    }
  }
  for (let i = 0; i < spec.ecCodewordsPerBlock; i += 1) {
    for (const block of ecBlocks) result[cursor++] = block[i] as number;
  }
  return result;
}

/* ------------------------------------------------------------------ *
 * 矩阵布局
 * ------------------------------------------------------------------ */

interface Grid {
  size: number;
  /** 模块取值：0 浅色，1 深色 */
  modules: Uint8Array;
  /** 是否为功能图形（不可被数据覆盖） */
  reserved: Uint8Array;
}

function createGrid(version: number): Grid {
  const size = version * 4 + 17;
  return { size, modules: new Uint8Array(size * size), reserved: new Uint8Array(size * size) };
}

function setModule(grid: Grid, row: number, col: number, dark: boolean, functional = true): void {
  const index = row * grid.size + col;
  grid.modules[index] = dark ? 1 : 0;
  if (functional) grid.reserved[index] = 1;
}

function placeFinder(grid: Grid, row: number, col: number): void {
  for (let r = -1; r <= 7; r += 1) {
    for (let c = -1; c <= 7; c += 1) {
      const rr = row + r;
      const cc = col + c;
      if (rr < 0 || cc < 0 || rr >= grid.size || cc >= grid.size) continue;
      const isBorder = r === 0 || r === 6 || c === 0 || c === 6;
      const isCore = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      setModule(grid, rr, cc, isBorder || isCore);
    }
  }
}

function placeAlignment(grid: Grid, centerRow: number, centerCol: number): void {
  for (let r = -2; r <= 2; r += 1) {
    for (let c = -2; c <= 2; c += 1) {
      const isDark = Math.max(Math.abs(r), Math.abs(c)) !== 1;
      setModule(grid, centerRow + r, centerCol + c, isDark);
    }
  }
}

function placeFunctionPatterns(grid: Grid, version: number): void {
  // 定位图形（三个角）与分隔符。
  placeFinder(grid, 0, 0);
  placeFinder(grid, 0, grid.size - 7);
  placeFinder(grid, grid.size - 7, 0);

  // 定时图形。
  for (let i = 8; i < grid.size - 8; i += 1) {
    const dark = i % 2 === 0;
    setModule(grid, 6, i, dark);
    setModule(grid, i, 6, dark);
  }

  // 校正图形：跳过与定位图形重叠的位置。
  const centers = ALIGNMENT_CENTERS[version - 1] ?? [];
  for (const row of centers) {
    for (const col of centers) {
      const overlapsFinder =
        (row <= 8 && col <= 8) ||
        (row <= 8 && col >= grid.size - 9) ||
        (row >= grid.size - 9 && col <= 8);
      if (overlapsFinder) continue;
      placeAlignment(grid, row, col);
    }
  }

  // 预留格式信息区（真正写入要等掩码定下来）。
  for (let i = 0; i < 9; i += 1) {
    if (i !== 6) {
      reserve(grid, 8, i);
      reserve(grid, i, 8);
    }
  }
  for (let i = 0; i < 8; i += 1) {
    reserve(grid, 8, grid.size - 1 - i);
    reserve(grid, grid.size - 1 - i, 8);
  }
  // 固定深色模块。
  setModule(grid, 4 * version + 9, 8, true);

  // 版本信息（版本 ≥ 7）。
  if (version >= 7) {
    for (let i = 0; i < 6; i += 1) {
      for (let j = 0; j < 3; j += 1) {
        reserve(grid, grid.size - 11 + j, i);
        reserve(grid, i, grid.size - 11 + j);
      }
    }
  }
}

function reserve(grid: Grid, row: number, col: number): void {
  grid.reserved[row * grid.size + col] = 1;
}

/** 数据位按「右下起、两列一组、上下蛇形」的固定规则填充。 */
function placeData(grid: Grid, codewords: Uint8Array): void {
  let bitIndex = 0;
  let upward = true;

  for (let col = grid.size - 1; col > 0; col -= 2) {
    // 第 6 列是定时图形，整列跳过。
    if (col === 6) col -= 1;

    for (let step = 0; step < grid.size; step += 1) {
      const row = upward ? grid.size - 1 - step : step;
      for (let offset = 0; offset < 2; offset += 1) {
        const cc = col - offset;
        const index = row * grid.size + cc;
        if (grid.reserved[index] === 1) continue;

        const byte = codewords[bitIndex >> 3];
        const bit = byte === undefined ? 0 : (byte >> (7 - (bitIndex & 7))) & 1;
        grid.modules[index] = bit;
        bitIndex += 1;
      }
    }
    upward = !upward;
  }
}

const MASK_FUNCTIONS: readonly ((row: number, col: number) => boolean)[] = [
  (row, col) => (row + col) % 2 === 0,
  (row) => row % 2 === 0,
  (_row, col) => col % 3 === 0,
  (row, col) => (row + col) % 3 === 0,
  (row, col) => (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0,
  (row, col) => ((row * col) % 2) + ((row * col) % 3) === 0,
  (row, col) => (((row * col) % 2) + ((row * col) % 3)) % 2 === 0,
  (row, col) => (((row + col) % 2) + ((row * col) % 3)) % 2 === 0,
];

function applyMask(grid: Grid, maskIndex: number): void {
  const mask = MASK_FUNCTIONS[maskIndex] as (row: number, col: number) => boolean;
  for (let row = 0; row < grid.size; row += 1) {
    for (let col = 0; col < grid.size; col += 1) {
      const index = row * grid.size + col;
      if (grid.reserved[index] === 1) continue;
      if (mask(row, col)) grid.modules[index] = (grid.modules[index] as number) ^ 1;
    }
  }
}

/** 格式信息：5 位数据 + BCH(15,5) 校验，再与固定掩码异或。 */
function formatInfoBits(ecc: EccLevel, maskIndex: number): number {
  const eccBits = ecc === 'L' ? 0b01 : 0b00;
  const data = (eccBits << 3) | maskIndex;

  let remainder = data;
  for (let i = 0; i < 10; i += 1) {
    remainder = (remainder << 1) ^ ((remainder >> 9) * 0x537);
  }
  return ((data << 10) | remainder) ^ FORMAT_INFO_MASK;
}

function placeFormatInfo(grid: Grid, ecc: EccLevel, maskIndex: number): void {
  const bits = formatInfoBits(ecc, maskIndex);
  const size = grid.size;

  for (let i = 0; i < 15; i += 1) {
    const dark = ((bits >> i) & 1) === 1;

    // 左上角：沿第 8 列向上，绕开第 6 行。
    if (i < 6) setModule(grid, i, 8, dark);
    else if (i < 8) setModule(grid, i + 1, 8, dark);
    else setModule(grid, 8, 15 - i, dark);

    // 右上 / 左下。
    if (i < 8) setModule(grid, 8, size - 1 - i, dark);
    else setModule(grid, size - 15 + i, 8, dark);
  }
}

function placeVersionInfo(grid: Grid, version: number): void {
  const bits = VERSION_INFO[version];
  if (bits === undefined) return;
  for (let i = 0; i < 18; i += 1) {
    const dark = ((bits >> i) & 1) === 1;
    const row = Math.floor(i / 3);
    const col = i % 3;
    setModule(grid, grid.size - 11 + col, row, dark);
    setModule(grid, row, grid.size - 11 + col, dark);
  }
}

/* ------------------------------------------------------------------ *
 * 掩码评估
 * ------------------------------------------------------------------ */

function penaltyScore(grid: Grid): number {
  const size = grid.size;
  const at = (row: number, col: number): number => grid.modules[row * size + col] as number;
  let score = 0;

  // 规则 1：同色连续 5 个以上。
  for (let i = 0; i < size; i += 1) {
    for (const horizontal of [true, false]) {
      let run = 1;
      for (let j = 1; j < size; j += 1) {
        const current = horizontal ? at(i, j) : at(j, i);
        const previous = horizontal ? at(i, j - 1) : at(j - 1, i);
        if (current === previous) {
          run += 1;
        } else {
          if (run >= 5) score += 3 + (run - 5);
          run = 1;
        }
      }
      if (run >= 5) score += 3 + (run - 5);
    }
  }

  // 规则 2：2×2 同色方块。
  for (let row = 0; row < size - 1; row += 1) {
    for (let col = 0; col < size - 1; col += 1) {
      const value = at(row, col);
      if (value === at(row, col + 1) && value === at(row + 1, col) && value === at(row + 1, col + 1)) {
        score += 3;
      }
    }
  }

  // 规则 3：形如 1:1:3:1:1 的类定位图形（两侧各留 4 个浅色）。
  const needle = [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0];
  const reversed = [...needle].reverse();
  for (let i = 0; i < size; i += 1) {
    for (let j = 0; j <= size - needle.length; j += 1) {
      for (const pattern of [needle, reversed]) {
        let matchRow = true;
        let matchCol = true;
        for (let k = 0; k < needle.length; k += 1) {
          if (at(i, j + k) !== pattern[k]) matchRow = false;
          if (at(j + k, i) !== pattern[k]) matchCol = false;
        }
        if (matchRow) score += 40;
        if (matchCol) score += 40;
      }
    }
  }

  // 规则 4：深色比例偏离 50% 的程度。
  let dark = 0;
  for (const module of grid.modules) dark += module;
  const percent = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;

  return score;
}

/* ------------------------------------------------------------------ *
 * 入口
 * ------------------------------------------------------------------ */

function selectVersion(byteLength: number, ecc: EccLevel): number {
  const table = ECC_TABLE[ecc];
  for (let version = 1; version <= MAX_VERSION; version += 1) {
    const spec = table[version - 1] as VersionSpec;
    // 字节模式的位数开销：4 位模式 + 计数指示符 + 数据 + 最多 4 位结束符。
    const countBits = version <= 9 ? 8 : 16;
    const neededBits = 4 + countBits + byteLength * 8;
    if (neededBits <= dataCapacity(spec) * 8) return version;
  }
  throw new Error(
    `内容过长：${byteLength} 字节超出纠错等级 ${ecc} 在版本 ${MAX_VERSION} 下的容量`,
  );
}

/** 把一个文本编码成 QR 模块矩阵。 */
export function encodeQr(text: string, ecc: EccLevel = 'M'): QrMatrix {
  const bytes = new TextEncoder().encode(text);
  const version = selectVersion(bytes.length, ecc);
  const spec = ECC_TABLE[ecc][version - 1] as VersionSpec;
  const codewords = buildCodewords(bytes, spec, version);

  // 逐个掩码试算，取惩罚分最低的那个 —— 这是规范做法，也直接影响能否被扫出来。
  let best: { matrix: QrMatrix; score: number } | null = null;
  for (let maskIndex = 0; maskIndex < 8; maskIndex += 1) {
    const grid = createGrid(version);
    placeFunctionPatterns(grid, version);
    placeData(grid, codewords);
    applyMask(grid, maskIndex);
    placeFormatInfo(grid, ecc, maskIndex);
    placeVersionInfo(grid, version);

    const score = penaltyScore(grid);
    if (!best || score < best.score) {
      best = {
        matrix: { size: grid.size, modules: grid.modules, version, ecc },
        score,
      };
    }
  }

  if (!best) throw new Error('QR 编码失败：没有任何可用掩码');
  return best.matrix;
}
